// conversations.js - Handles conversation management

import store from './store.js';
import { encryptData, decryptData, showConfirm, showNotification, formatMessageContent } from './utils.js';
import { apiUrl } from './api-settings.js';

/**
 * Sets up conversation management and UI
 */
function setupConversations() {
    const newChatBtn = document.getElementById("newChatBtn");
    const conversationsList = document.getElementById("conversationsList");

    if (newChatBtn) {
        newChatBtn.addEventListener("click", createNewConversation);
    }

    loadConversations();
    renderConversationsList();

    if (store.getConversations().length === 0) {
        createNewConversation();
    } else {
        loadConversation(store.getConversations()[0].id);
    }
}

/**
 * Creates a new conversation
 * @returns {string} The ID of the new conversation
 */
function createNewConversation() {
    const id = Date.now().toString();
    const title = "New Conversation";
    const messages = [
        {
            role: "assistant",
            content: "Hey! I'm Alaric, your AI research agent. What's on your mind?",
            timestamp: new Date().toISOString()
        },
    ];

    const newConversation = {
        id,
        title,
        messages,
        createdAt: new Date().toISOString(),
    };

    const conversations = store.getConversations();
    conversations.unshift(newConversation);
    store.setConversations(conversations);
    store.setCurrentConversationId(id);
    saveConversations();

    const messagesContainer = document.getElementById("messagesContainer");
    if (messagesContainer) {
       renderMessages(messages);
    }
    renderConversationsList();

    console.log(`Conversation ${id} created and set as current.`);
    return id;
}

/**
 * Loads a conversation by ID
 * @param {string} id - The conversation ID
 */
function loadConversation(id) {
    store.setCurrentConversationId(id);
    const conversation = store.getConversations().find((c) => c.id === id);

    if (conversation) {
        renderMessages(conversation.messages);
        renderConversationsList();

        const sendBtn = document.getElementById("sendBtn");
        const messageInput = document.getElementById("messageInput");
        const pendingResponses = store.getPendingResponses();
        
        if (pendingResponses && pendingResponses[id]) {
            addThinkingIndicator(id);
            if (sendBtn) sendBtn.disabled = true;
        } else {
            if (sendBtn && messageInput) sendBtn.disabled = messageInput.value.trim() === "";
        }
    }
}

function saveConversations() {
    if (store.getIsGuestMode()) {
        localStorage.setItem(
            "quantumGuestConversations",
            JSON.stringify(store.getConversations())
        );
    } else if (store.getWalletAddress()) {
        // Determine the key based on account type
        let storageKey;
        let encryptionKey;
        
        if (store.getAccountType() === 'google' && store.getUserEmail()) {
            // For Google accounts, use email as the identifier
            storageKey = `quantumGoogleConversations_${store.getUserEmail()}`;
            encryptionKey = store.getUserEmail();
        } else {
            // For wallet-based accounts, use wallet address
            storageKey = `quantumWalletConversations_${store.getWalletAddress()}`;
            encryptionKey = store.getWalletAddress();
        }

        const encryptedData = encryptData(
            store.getConversations(),
            encryptionKey
        );
        if (encryptedData) {
            localStorage.setItem(storageKey, encryptedData);
        }
    }
}

// Mise à jour de la fonction loadConversations dans conversations.js
function loadConversations() {
    let conversations = [];

    if (store.getIsGuestMode()) {
        const savedGuestConversations = localStorage.getItem("quantumGuestConversations");
        
        if (savedGuestConversations) {
            try {
                conversations = JSON.parse(savedGuestConversations);
            } catch (error) {
                console.error("Erreur lors du chargement des conversations:", error);
            }
        }
    } else if (store.getWalletAddress()) {
        let storageKey;
        let decryptionKey;
        
        if (store.getAccountType() === 'google' && store.getUserEmail()) {
            // For Google accounts, use email as the identifier
            storageKey = `quantumGoogleConversations_${store.getUserEmail()}`;
            decryptionKey = store.getUserEmail();
        } else {
            // For wallet-based accounts, use wallet address
            storageKey = `quantumWalletConversations_${store.getWalletAddress()}`;
            decryptionKey = store.getWalletAddress();
        }
        
        const encryptedData = localStorage.getItem(storageKey);

        if (encryptedData) {
            const decryptedConversations = decryptData(
                encryptedData,
                decryptionKey
            );
            if (decryptedConversations) {
                conversations = decryptedConversations;
            }
        }
    }

    store.setConversations(conversations);
    renderConversationsList();
}

/**
 * Renders the conversations list in the sidebar
 */
function renderConversationsList() {
    const conversationsList = document.getElementById("conversationsList");
    if (!conversationsList) return;
    
    conversationsList.innerHTML = "";
    const conversations = store.getConversations();
    const currentConversationId = store.getCurrentConversationId();

    conversations.forEach((conversation) => {
        const item = document.createElement("div");
        item.className = "conversation-item";
        item.dataset.id = conversation.id;
        
        if (conversation.id === currentConversationId) {
            item.classList.add("active");
        }

        const icon = document.createElement("div");
        icon.className = "conversation-icon";
        icon.innerHTML = `
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
            </svg>
        `;

        const text = document.createElement("div");
        text.className = "conversation-item-text";
        text.textContent = conversation.title;

        const deleteBtn = document.createElement("div");
        deleteBtn.className = "conversation-delete";
        deleteBtn.textContent = "×";
        deleteBtn.title = "Delete conversation";
        deleteBtn.addEventListener("click", (e) => {
            e.stopPropagation();
            deleteConversation(conversation.id);
        });

        item.appendChild(icon);
        item.appendChild(text);
        item.appendChild(deleteBtn);
        item.addEventListener("click", () => loadConversation(conversation.id));

        conversationsList.appendChild(item);
    });
}

/**
 * Renders messages in the message container
 * @param {Array} messages - The messages to render
 */
function renderMessages(messages) {
    const messagesContainer = document.getElementById("messagesContainer");
    if (!messagesContainer) return;
    
    messagesContainer.innerHTML = "";

    messages.forEach((message) => {
        const messageElement = document.createElement("div");
        messageElement.className = "message";
        messageElement.id = `message-${message.timestamp || Date.now()}-${Math.random().toString(36).substring(2, 9)}`; // Ensure unique ID

        let avatar;
        if (message.role === "user") {
            avatar = document.createElement("div");
            avatar.className = "avatar user-avatar";
            avatar.textContent = "Y"; // Placeholder text, styled by CSS
        } else { // Assuming 'assistant' role
            avatar = document.createElement("img");
            avatar.className = "avatar ai-avatar";
            avatar.src = "assets/pp/alaric.png"; // Path to AI profile picture
            avatar.alt = "Alaric Avatar";
        }

        const content = document.createElement("div");
        content.className = "message-content";
        
        // Check if there's custom HTML to render
        if (message.customHTML) {
            // If customHTML exists, render *only* that inside the content div
            content.innerHTML = message.customHTML;
        } else if (message.isHtml) {
            // Regular HTML content (no customHTML)
            content.innerHTML = message.content;
        } else {
            // Plain text content
            content.innerHTML = formatMessageContent(message.content);
        }

        messageElement.appendChild(avatar);
        messageElement.appendChild(content);

        if (message.role === "assistant") {
            const actionsDiv = document.createElement("div");
            actionsDiv.className = "message-actions";
            
            const menuBtn = document.createElement("button");
            menuBtn.className = "message-menu-btn";
            menuBtn.textContent = "⚡";
            
            const menu = document.createElement("div");
            menu.className = "message-menu";
            
            const reportItem = document.createElement("div");
            reportItem.className = "message-menu-item report-item";
            
            const iconSpan = document.createElement("span");
            iconSpan.textContent = "⚡ "; // Ajouter un espace
            
            reportItem.appendChild(iconSpan);
            reportItem.appendChild(document.createTextNode("Report inaccurate content"));
            
            menu.appendChild(reportItem);
            actionsDiv.appendChild(menuBtn);
            actionsDiv.appendChild(menu);
            
            // Écouteur pour le bouton du menu
            menuBtn.addEventListener("click", (e) => {
                console.log("Menu button clicked directly on element:", messageElement.id);
                // Fermer tous les autres menus actifs
                document.querySelectorAll('.message-menu.active').forEach(otherMenu => {
                    if (otherMenu !== menu) {
                        otherMenu.classList.remove('active');
                    }
                });
                // Basculer le menu actuel
                menu.classList.toggle("active");
                e.stopPropagation(); // Empêche la fermeture immédiate par l'écouteur global
            });
            
            // Écouteur pour l'item de rapport
            reportItem.addEventListener("click", (e) => {
                console.log("Report item clicked directly on element:", messageElement.id);
                reportInaccurateContent(messageElement); // Appelle la fonction de rapport existante
                menu.classList.remove("active"); // Ferme le menu après le clic
                e.stopPropagation();
            });
            
            messageElement.appendChild(actionsDiv);
        }

        messagesContainer.appendChild(messageElement);
    });

    // Écouteur global pour fermer les menus si on clique ailleurs
    document.addEventListener('click', (e) => {
        if (!e.target.closest('.message-menu') && !e.target.closest('.message-menu-btn')) {
            document.querySelectorAll('.message-menu.active').forEach(menu => {
                menu.classList.remove('active');
            });
        }
    }, true); // Utiliser la capture pour s'assurer qu'il s'exécute avant d'autres arrêts de propagation

    messagesContainer.scrollTop = messagesContainer.scrollHeight;
}

/**
 * Deletes a conversation
 * @param {string} id - The ID of the conversation to delete
 */
function deleteConversation(id) {
    showConfirm(
        "Delete Conversation",
        "Are you sure you want to delete this conversation? This action cannot be undone.",
        (confirmed) => {
            if (confirmed) {
                const conversations = store.getConversations().filter((c) => c.id !== id);
                store.setConversations(conversations);
                saveConversations();

                if (id === store.getCurrentConversationId()) {
                    if (conversations.length > 0) {
                        loadConversation(conversations[0].id);
                    } else {
                        createNewConversation();
                    }
                }

                renderConversationsList();
            }
        }
    );
}

/**
 * Reports a message as inaccurate
 * @param {HTMLElement} messageElement - The message element to report
 */
function reportInaccurateContent(messageElement) {
    console.log("reportInaccurateContent called with:", messageElement);
    const reportModal = document.getElementById("reportModal");
    let reportCancel = document.getElementById("reportCancel"); // Utiliser let
    let reportSubmit = document.getElementById("reportSubmit"); // Utiliser let
    const reportReason = document.getElementById("reportReason");
    
    console.log("Modal elements:", {
        reportModal,
        reportCancel,
        reportSubmit,
        reportReason
    });

    if (!reportModal || !reportCancel || !reportSubmit || !reportReason) {
        console.error("Report modal elements not found!");
        return;
    }
    
    // --- Début de la correction : Nettoyage des écouteurs --- 
    // Cloner les boutons pour supprimer tous les écouteurs précédents
    const newReportCancel = reportCancel.cloneNode(true);
    reportCancel.parentNode.replaceChild(newReportCancel, reportCancel);
    reportCancel = newReportCancel; // Mettre à jour la référence

    const newReportSubmit = reportSubmit.cloneNode(true);
    reportSubmit.parentNode.replaceChild(newReportSubmit, reportSubmit);
    reportSubmit = newReportSubmit; // Mettre à jour la référence
    // --- Fin de la correction --- 

    // Store the message element for use in the callback
    reportModal.dataset.messageElement = messageElement.id || `message-${Date.now()}`;
    if (!messageElement.id) {
        messageElement.id = reportModal.dataset.messageElement;
    }
    
    // Clear previous reason
    reportReason.value = "";
    
    // Show the report modal
    reportModal.style.display = "flex";
    console.log("Report modal should be visible now");
    
    // Définir les handlers
    const cancelHandler = () => {
        console.log("Cancel clicked");
        reportModal.style.display = "none";
        // Pas besoin de removeEventListener ici car les boutons sont clonés
    };
    
    const submitHandler = async () => {
        console.log("Submit clicked");
        
        const reason = reportReason.value.trim();
        if (!reason) {
            showNotification("Please provide a reason for your report.", "error");
             // Ne pas cacher le modal ici pour que l'utilisateur puisse corriger
            return; 
        }
        
        // Cacher le modal seulement après validation réussie
        reportModal.style.display = "none";
        
        const conversations = store.getConversations();
        const currentConversationId = store.getCurrentConversationId();
        const currentConversation = conversations.find(
            (c) => c.id === currentConversationId
        );

        // Récupérer l'élément message associé (stocké dans dataset)
        const storedMessageId = reportModal.dataset.messageElement;
        const actualMessageElement = document.getElementById(storedMessageId);

        if (!currentConversation || !actualMessageElement) {
            console.error("Conversation or original message element not found for reporting.");
            showNotification("Error preparing report data.", "error");
            return;
        }

        try {
            // Dynamically import to avoid circular dependency
            const { apiUrl, contextMessageCount } = await import('./api-settings.js');
            
            const messageContent = actualMessageElement
                .querySelector(".message-content")
                ?.textContent.trim();
            
            if (!messageContent) {
                 console.error("Could not extract message content for reporting.");
                 showNotification("Error extracting message content.", "error");
                 return;
            }

            // Find the index of the current message
            const messageIndex = currentConversation.messages.findIndex(
                 // Comparaison plus robuste, peut-être stocker l'index ou un ID unique du message?
                 m => m.content === messageContent && m.role === "assistant"
             );

            if (messageIndex === -1) {
                console.error("Could not find the reported message in the conversation history.");
                showNotification("Error locating message in history.", "error");
                return;
            }
            
            // Correction: Get the reported message + 2 previous ones (total 3)
            const startIndex = Math.max(0, messageIndex - 2); 
            const contextMessages = currentConversation.messages.slice(
                startIndex, messageIndex + 1
            );

            const authToken = localStorage.getItem("quantumAuthToken");
            if (!authToken) {
                 // Peut-être rediriger vers la connexion ou afficher un message plus clair
                showNotification("Authentication required. Please reconnect.", "error");
                // Import dynamique pour éviter dépendance circulaire si nécessaire
                // const { performLogout } = await import("./authentication.js"); 
                // performLogout();
                return;
            }

            const response = await fetch(`${apiUrl}/report-inaccurate`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${authToken}`,
                },
                body: JSON.stringify({
                    messageContent: messageContent,
                    context: contextMessages,
                    reason: reason,
                    walletAddress: store.getWalletAddress(),
                    timestamp: new Date().toISOString(),
                }),
            });

            if (!response.ok) {
                let errorData = { error: "Failed to submit report. Unknown error." };
                try {
                    errorData = await response.json();
                } catch(e) { /* Ignorer l'erreur de parsing JSON */ }
                console.error("Report submission failed:", errorData);
                throw new Error(errorData.error || "Failed to submit report");
            }

            showNotification(
                "Thank you for your feedback. Our team will review this response.",
                "success"
            );
        } catch (error) {
            console.error("Error reporting inaccurate content:", error);
            showNotification(`Error submitting report: ${error.message}`, "error");
        }
        // Pas besoin de removeEventListener ici car les boutons sont clonés
    };
    
    // Ajouter les écouteurs aux NOUVEAUX boutons clonés
    reportCancel.addEventListener("click", cancelHandler);
    reportSubmit.addEventListener("click", submitHandler);
}

/**
 * Add a "thinking" indicator to show the AI is processing
 * @param {string} conversationId - ID of the current conversation
 */
function addThinkingIndicator(conversationId) {
    const messagesContainer = document.getElementById("messagesContainer");
    console.log("addThinkingIndicator: Adding indicator for conversation:", conversationId); // Log entry
    if (conversationId === store.getCurrentConversationId()) {
        const typingIndicator = document.createElement("div");
        typingIndicator.className = "message thinking-indicator";
        typingIndicator.id = `thinking-${conversationId}`;
        
        const avatar = document.createElement("img");
        avatar.className = "avatar ai-avatar";
        avatar.src = "assets/pp/alaric.png";
        avatar.alt = "Alaric Avatar Thinking";
        
        const typingContent = document.createElement("div");
        typingContent.className = "message-content";
        typingContent.textContent = "Thinking";
        
        const dotsSpan = document.createElement("span");
        dotsSpan.className = "typing-dots";
        dotsSpan.textContent = "";
        
        typingContent.appendChild(dotsSpan);
        typingIndicator.appendChild(avatar);
        typingIndicator.appendChild(typingContent);
        
        messagesContainer.appendChild(typingIndicator);
        messagesContainer.scrollTop = messagesContainer.scrollHeight;
        
        let dotCount = 0;
        const dotAnimation = setInterval(() => {
            const indicator = document.getElementById(
                `thinking-${conversationId}`
            );
            if (indicator) {
                const dotsElement = indicator.querySelector(".typing-dots");
                if (dotsElement) {
                    dotCount = (dotCount % 3) + 1;
                    dotsElement.textContent = ".".repeat(dotCount);
                }
            } else {
                clearInterval(dotAnimation);
            }
        }, 700);
        
        typingIndicator.dataset.animationId = dotAnimation;
    }
}

/**
 * Remove the "thinking" indicator
 * @param {string} conversationId - ID of the current conversation
 */
function removeThinkingIndicator(conversationId) {
    const messagesContainer = document.getElementById("messagesContainer");
    console.log("removeThinkingIndicator: Attempting to remove indicator for conversation:", conversationId); // Log entry
    if (conversationId === store.getCurrentConversationId()) {
        const indicator = document.getElementById(
            `thinking-${conversationId}`
        );
        console.log("removeThinkingIndicator: Indicator found:", !!indicator); // Log if found
        if (indicator) {
            if (indicator.dataset.animationId) {
                clearInterval(parseInt(indicator.dataset.animationId));
            }
            messagesContainer.removeChild(indicator);
        }
    }
}

/**
 * Get conversations array
 * @returns {Array} - The conversations array
 */
function getConversations() {
    return store.getConversations();
}

/**
 * Creates HTML for wallet scan results to display in chat
 * @param {Object} result - The scan result object
 * @returns {string} - HTML string to render in the chat
 */
function createWalletScanResultHTML(result) {
    const { wallet, content } = result;
    
    if (!wallet || !content) {
        return '<div class="scan-error">Error: Invalid scan result data</div>';
    }

    // Format currency with appropriate decimals
    const formatCurrency = (value) => {
        if (value >= 1000000) {
            return `$${(value / 1000000).toFixed(2)}M`;
        } else if (value >= 1000) {
            return `$${(value / 1000).toFixed(2)}K`;
        } else {
            // Check if the value has decimals
            return Number.isInteger(value) ? `$${value}` : `$${value.toFixed(2)}`;
        }
    };
    
    // Format BTC value (convert from satoshi to BTC)
    const formatBtc = (satoshis) => {
        if (satoshis < 100000) {
            return `${satoshis.toLocaleString()} sats`;
        } else {
            const btc = satoshis / 100000000;
            if (btc >= 1000) {
                return `${parseFloat(btc.toFixed(2))} BTC`;
            } else if (btc >= 100) {
                return `${parseFloat(btc.toFixed(3))} BTC`;
            } else if (btc >= 10) {
                return `${parseFloat(btc.toFixed(4))} BTC`;
            } else if (btc >= 1) {
                return `${parseFloat(btc.toFixed(5))} BTC`;
            } else {
                return `${parseFloat(btc.toFixed(6))} BTC`;
            }
        }
    };

    // Extract summary data
    const summary = content.summary || {};
    const totalValueBtc = summary.totalValueBtc || 0;
    const totalValueUsd = summary.totalValueUsd || 0;
    const totalAssetCount = summary.tokenCount || 0;
    const protocolStats = summary.protocolStats || {};
    
    // Start building the compact HTML with improved styling
    let html = `<div class="wallet-scan-compact" style="border-radius: 12px; border: 1px solid var(--border-color); overflow: hidden;">
        <div class="wallet-header" style="background: var(--bg-darker); padding: 12px; border-bottom: 1px solid var(--border-color);">
            <div class="wallet-address" style="font-weight: bold; margin-bottom: 6px;"><strong>Wallet:</strong> <span style="color: var(--accent-light)">${wallet.address}</span></div>
            <div class="total-summary"><strong>Total Value:</strong> <span style="color: var(--accent-light)">${formatBtc(totalValueBtc)} - ${formatCurrency(totalValueUsd)}</span></div>
        </div>
        <div class="wallet-content" style="padding: 12px;">
    `;
    
    const pipeStats = protocolStats.pipe || {};
    if (content.pipe && content.pipe.length > 0) {
        html += `<div class="protocol-section" style="margin-bottom: 16px; background: rgba(var(--bg-color-rgb), 0.5); padding: 10px; border-radius: 8px; border-left: 3px solid var(--accent-light);">
            <div class="protocol-header" style="font-weight: bold; margin-bottom: 8px;"><strong>PIPE (<span style="color: var(--accent-light)">${content.pipe.length}</span>)</strong> - <span style="color: var(--accent-light)">${formatBtc(pipeStats.totalValueBtc ?? 0)} - ${formatCurrency(pipeStats.totalValueUsd || 0)}</span></div>
            <div class="tokens-list" style="padding-left: 10px;">`;
        
        // Sort pipe tokens by total value in descending order
        const sortedPipe = [...content.pipe].sort((a, b) => b.totalValue - a.totalValue);
        
        sortedPipe.forEach(pipe => {
            const btcPrice = pipe.unitPrice;
            const btcTotal = pipe.totalValue;
            html += `<div class="token" style="margin-bottom: 6px; padding: 4px 0; border-bottom: 1px dotted var(--border-color);">
                        <span class="token-name">${pipe.ticker.toUpperCase()} (<span style="color: var(--accent-light)">${pipe.quantity.toLocaleString()}</span>)</span>
                        <span class="token-price">Unit: ${formatBtc(btcPrice)} / ${formatCurrency(pipe.unitPriceUsd)}</span>
                        <span class="token-value">Total: <span style="color: var(--accent-light)">${formatBtc(btcTotal)} / ${formatCurrency(pipe.totalValueUsd)}</span></span>
                    </div>`;
        });
        
        html += `</div></div>`;
    } else {
        html += `<div class="protocol-section" style="margin-bottom: 16px; background: rgba(var(--bg-color-rgb), 0.5); padding: 10px; border-radius: 8px; border-left: 3px solid var(--border-color);">
            <div class="protocol-header"><strong>PIPE (<span style="color: var(--accent-light)">0</span>)</strong></div>
        </div>`;
    }
    
    const brc20Stats = protocolStats.brc20 || {};
    if (content.brc20 && content.brc20.length > 0) {
        html += `<div class="protocol-section" style="margin-bottom: 16px; background: rgba(var(--bg-color-rgb), 0.5); padding: 10px; border-radius: 8px; border-left: 3px solid #f7931a;">
            <div class="protocol-header" style="font-weight: bold; margin-bottom: 8px;"><strong>BRC-20 (<span style="color: var(--accent-light)">${content.brc20.length}</span>)</strong> - <span style="color: var(--accent-light)">${formatBtc(brc20Stats.totalValueBtc ?? 0)} - ${formatCurrency(brc20Stats.totalValueUsd ?? 0)}</span></div>
            <div class="tokens-list" style="padding-left: 10px;">`;
        
        // Sort tokens by total value in descending order
        const sortedBrc20 = [...content.brc20].sort((a, b) => b.totalValue - a.totalValue);
        
        sortedBrc20.forEach(token => {
            const btcPrice = token.unitPrice;
            const btcTotal = token.totalValue;
            html += `<div class="token" style="margin-bottom: 6px; padding: 4px 0; border-bottom: 1px dotted var(--border-color);">
                        <span class="token-name">${token.ticker.toUpperCase()} (<span style="color: var(--accent-light)">${token.quantity.toLocaleString()}</span>)</span>
                        <span class="token-price">Unit: ${formatBtc(btcPrice)} / ${formatCurrency(token.unitPriceUsd)}</span>
                        <span class="token-value">Total: <span style="color: var(--accent-light)">${formatBtc(btcTotal)} / ${formatCurrency(token.totalValueUsd)}</span></span>
                    </div>`;
        });
        
        html += `</div></div>`;
    } else {
        html += `<div class="protocol-section" style="margin-bottom: 16px; background: rgba(var(--bg-color-rgb), 0.5); padding: 10px; border-radius: 8px; border-left: 3px solid #f7931a;">
            <div class="protocol-header"><strong>BRC-20 (<span style="color: var(--accent-light)">0</span>)</strong></div>
        </div>`;
    }

    const runesStats = protocolStats.runes || {};
    if (content.runes && content.runes.length > 0) {
        html += `<div class="protocol-section" style="margin-bottom: 16px; background: rgba(var(--bg-color-rgb), 0.5); padding: 10px; border-radius: 8px; border-left: 3px solid #6b5b95;">
            <div class="protocol-header" style="font-weight: bold; margin-bottom: 8px;"><strong>RUNES (<span style="color: var(--accent-light)">${content.runes.length}</span>)</strong> - <span style="color: var(--accent-light)">${formatBtc(runesStats.totalValueBtc ?? 0)} - ${formatCurrency(runesStats.totalValueUsd || 0)}</span></div>
            <div class="tokens-list" style="padding-left: 10px;">`;
        
        // Sort runes by total value in descending order
        const sortedRunes = [...content.runes].sort((a, b) => b.totalValue - a.totalValue);
        
        sortedRunes.forEach(rune => {
            const btcPrice = rune.unitPrice;
            const btcTotal = rune.totalValue;
            const symbolDisplay = rune.symbol ? ` ${rune.symbol}` : '';
            html += `<div class="token" style="margin-bottom: 6px; padding: 4px 0; border-bottom: 1px dotted var(--border-color);">
                        <span class="token-name">${rune.ticker.toUpperCase()}${symbolDisplay} (<span style="color: var(--accent-light)">${rune.quantity.toLocaleString()}</span>)</span>
                        <span class="token-price">Unit: ${formatBtc(btcPrice)} / ${formatCurrency(rune.unitPriceUsd)}</span>
                        <span class="token-value">Total: <span style="color: var(--accent-light)">${formatBtc(btcTotal)} / ${formatCurrency(rune.totalValueUsd)}</span></span>
                    </div>`;
        });
        
        html += `</div></div>`;
    } else {
        html += `<div class="protocol-section" style="margin-bottom: 16px; background: rgba(var(--bg-color-rgb), 0.5); padding: 10px; border-radius: 8px; border-left: 3px solid #6b5b95;">
            <div class="protocol-header"><strong>RUNES (<span style="color: var(--accent-light)">0</span>)</strong></div>
        </div>`;
    }

    // Add TAP assets section
    const tapStats = protocolStats.tap || {};
    if (content.tap && content.tap.length > 0) {
        html += `<div class="protocol-section" style="margin-bottom: 16px; background: rgba(var(--bg-color-rgb), 0.5); padding: 10px; border-radius: 8px; border-left: 3px solid #78c2ad;">
            <div class="protocol-header" style="font-weight: bold; margin-bottom: 8px;"><strong>TAP (<span style="color: var(--accent-light)">${content.tap.length}</span>)</strong> - <span style="color: var(--accent-light)">${formatBtc(tapStats.totalValueBtc ?? 0)} - ${formatCurrency(tapStats.totalValueUsd ?? 0)}</span></div>
            <div class="tokens-list" style="padding-left: 10px;">`;
        
        // Sort tap tokens by total value in descending order
        const sortedTap = [...content.tap].sort((a, b) => b.totalValue - a.totalValue);
        
        sortedTap.forEach(token => {
            const btcPrice = token.unitPrice;
            const btcTotal = token.totalValue;
            html += `<div class="token" style="margin-bottom: 6px; padding: 4px 0; border-bottom: 1px dotted var(--border-color);">
                        <span class="token-name">${token.ticker.toUpperCase()} (<span style="color: var(--accent-light)">${token.quantity.toLocaleString()}</span>)</span>
                        <span class="token-price">Unit: ${formatBtc(btcPrice)} / ${formatCurrency(token.unitPriceUsd)}</span>
                        <span class="token-value">Total: <span style="color: var(--accent-light)">${formatBtc(btcTotal)} / ${formatCurrency(token.totalValueUsd)}</span></span>
                    </div>`;
        });
        
        html += `</div></div>`;
    } else {
        html += `<div class="protocol-section" style="margin-bottom: 16px; background: rgba(var(--bg-color-rgb), 0.5); padding: 10px; border-radius: 8px; border-left: 3px solid #78c2ad;">
            <div class="protocol-header"><strong>TAP (<span style="color: var(--accent-light)">0</span>)</strong></div>
        </div>`;
    }
    
    html += `</div></div>`;
    return html;
}

/**
 * Creates HTML for scanning multiple wallets to display in chat
 * @param {Object} result - The scan result object with multiple wallets
 * @returns {string} - HTML string to render in the chat
 */
function createMultipleWalletScanResultHTML(result) {
    if (!result || !result.content || !Array.isArray(result.content)) {
        return '<div class="scan-error">Error: Invalid scan result data for multiple wallets</div>';
    }

    // Format currency with appropriate decimals
    const formatCurrency = (value) => {
        if (value >= 1000000) {
            return `$${(value / 1000000).toFixed(2)}M`;
        } else if (value >= 1000) {
            return `$${(value / 1000).toFixed(2)}K`;
        } else {
            // Check if the value has decimals
            return Number.isInteger(value) ? `$${value}` : `$${value.toFixed(2)}`;
        }
    };
    
    // Format BTC value (convert from satoshi to BTC)
    const formatBtc = (satoshis) => {
        if (satoshis < 100000) {
            return `${satoshis.toLocaleString()} sats`;
        } else {
            const btc = satoshis / 100000000;
            if (btc >= 1000) {
                return `${parseFloat(btc.toFixed(2))} BTC`;
            } else if (btc >= 100) {
                return `${parseFloat(btc.toFixed(3))} BTC`;
            } else if (btc >= 10) {
                return `${parseFloat(btc.toFixed(4))} BTC`;
            } else if (btc >= 1) {
                return `${parseFloat(btc.toFixed(5))} BTC`;
            } else {
                return `${parseFloat(btc.toFixed(6))} BTC`;
            }
        }
    };

    // Get overall summary if available
    const overallSummary = result.summary || {};
    const totalValueUsd = overallSummary.totalValueUsd || 0;
    const totalValueBtc = overallSummary.totalValueBtc || 0;
    const totalAssetCount = overallSummary.tokenCount || 0;
    const overallProtocolStats = overallSummary.protocolStats || {};

    // Utiliser le même style que le scan simple pour le conteneur principal
    let html = `<div class="wallet-scan-compact" style="border-radius: 12px; border: 1px solid var(--border-color); overflow: hidden;">
        <div class="portfolio-header" style="background: var(--bg-darker); padding: 12px; border-bottom: 1px solid var(--border-color);">
            <div class="total-summary" style="font-weight: bold;"><strong>Portfolio Value:</strong> <span style="color: var(--accent-light)">${formatBtc(totalValueBtc)} - ${formatCurrency(totalValueUsd)}</span></div>
        </div>
        <div class="portfolio-summary" style="padding: 12px; background: rgba(var(--bg-color-rgb), 0.3); border-bottom: 1px solid var(--border-color);">
    `;
    // Process the summary first
    if (overallProtocolStats) {        
        const pipeStats = overallProtocolStats.pipe || {};
        if (pipeStats.count > 0) {
            html += `<div class="token-summary" style="margin-bottom: 8px;"><strong>PIPE:</strong> <span style="color: var(--accent-light)">${pipeStats.count} tokens</span> - <span style="color: var(--accent-light)">${formatBtc(pipeStats.totalValueBtc ?? 0)} - ${formatCurrency(pipeStats.totalValueUsd)}</span></div>`;
        }
        
        const brc20Stats = overallProtocolStats.brc20 || {};
        if (brc20Stats.count > 0) {
            html += `<div class="token-summary" style="margin-bottom: 8px;"><strong>BRC-20:</strong> <span style="color: var(--accent-light)">${brc20Stats.count} tokens</span> - <span style="color: var(--accent-light)">${formatBtc(brc20Stats.totalValueBtc)} - ${formatCurrency(brc20Stats.totalValueUsd)}</span></div>`;
        }
        
        const runesStats = overallProtocolStats.runes || {};
        if (runesStats.count > 0) {
            html += `<div class="token-summary" style="margin-bottom: 8px;"><strong>RUNES:</strong> <span style="color: var(--accent-light)">${runesStats.count} tokens</span> - <span style="color: var(--accent-light)">${formatBtc(runesStats.totalValueBtc ?? 0)} - ${formatCurrency(runesStats.totalValueUsd)}</span></div>`;
        }
        
        // Add TAP protocol summary
        const tapStats = overallProtocolStats.tap || {};
        if (tapStats.count > 0) {
            html += `<div class="token-summary" style="margin-bottom: 8px;"><strong>TAP:</strong> <span style="color: var(--accent-light)">${tapStats.count} tokens</span> - <span style="color: var(--accent-light)">${formatBtc(tapStats.totalValueBtc)} - ${formatCurrency(tapStats.totalValueUsd)}</span></div>`;
        }
    }
    
    html += `</div><div class="wallets-container" style="padding: 12px;">`;

    // Process each wallet - using alternating background colors
    const walletColors = ['#2c3e50', '#34495e', '#1e272e', '#2d3436'];
    
    result.content.forEach((item, index) => {
        const { wallet, result: walletResult, error } = item;
        const walletBgColor = walletColors[index % walletColors.length];
        
        if (error) {
            html += `<div class="wallet-section" style="margin-bottom: 20px; border-radius: 8px; border: 1px solid var(--border-color); overflow: hidden;">
                <div class="wallet-header" style="background: ${walletBgColor}; padding: 12px; color: white;">
                    <div class="wallet-title" style="font-weight: bold;">Wallet #${index + 1} - ${wallet.network}</div>
                    <div class="wallet-error" style="color: #ff7979; margin-top: 5px;"><i>Error: ${error}</i></div>
                </div>
            </div>`;
            return;
        }
        
        if (!walletResult) {
            html += `<div class="wallet-section" style="margin-bottom: 20px; border-radius: 8px; border: 1px solid var(--border-color); overflow: hidden;">
                <div class="wallet-header" style="background: ${walletBgColor}; padding: 12px; color: white;">
                    <div class="wallet-title" style="font-weight: bold;">Wallet #${index + 1} - ${wallet.network}</div>
                    <div class="wallet-error" style="color: #ff7979; margin-top: 5px;"><i>Error: No data returned</i></div>
                </div>
            </div>`;
            return;
        }
        
        // Get wallet-specific summary
        const walletSummary = walletResult.summary || {};
        const walletValueUsd = walletSummary.totalValueUsd || 0;
        const walletValueBtc = walletSummary.totalValueBtc || 0;
        
        // Wallet header with distinct color
        html += `<div class="wallet-section" style="margin-bottom: 20px; border-radius: 8px; border: 1px solid var(--border-color); overflow: hidden;">
            <div class="wallet-header" style="background: ${walletBgColor}; padding: 12px; color: white;">
                <div class="wallet-title" style="font-weight: bold; margin-bottom: 4px;">Wallet #${index + 1} - ${wallet.address}</div>
                <div class="wallet-network" style="font-size: 0.9em; margin-bottom: 8px;">Network: ${wallet.network}</div>
                <div class="wallet-value">Total Value: <span style="color: #7bed9f;">${formatBtc(walletValueBtc)} - ${formatCurrency(walletValueUsd)}</span></div>
            </div>
            <div class="wallet-content" style="padding: 12px;">`;
        
        // PIPE Tokens
        const protocolStats = walletSummary.protocolStats || {};
        const pipeStats = protocolStats.pipe || {};
        if (walletResult.pipe && walletResult.pipe.length > 0) {
            html += `<div class="protocol-section" style="margin-bottom: 16px; background: rgba(var(--bg-color-rgb), 0.5); padding: 10px; border-radius: 8px; border-left: 3px solid var(--accent-light);">
                <div class="protocol-header" style="font-weight: bold; margin-bottom: 8px;"><strong>PIPE</strong> (<span style="color: var(--accent-light)">${walletResult.pipe.length}</span>) - <span style="color: var(--accent-light)">${formatBtc(pipeStats.totalValueBtc ?? 0)} - ${formatCurrency(pipeStats.totalValueUsd || 0)}</span></div>
                <div class="tokens-list" style="padding-left: 10px;">`;
            
            // Sort pipe tokens by total value in descending order
            const sortedPipe = [...walletResult.pipe].sort((a, b) => b.totalValue - a.totalValue);
            
            // Limiter à 2 tokens pour ne pas surcharger l'UI
            const topPipe = sortedPipe.slice(0, 2);
            topPipe.forEach(pipe => {
                const btcPrice = pipe.unitPrice;
                const btcTotal = pipe.totalValue;
                html += `<div class="token" style="margin-bottom: 6px; padding: 4px 0; border-bottom: 1px dotted var(--border-color);">
                            <span class="token-name">${pipe.ticker.toUpperCase()} (<span style="color: var(--accent-light)">${pipe.quantity.toLocaleString()}</span>)</span>
                            <span class="token-price">Unit: ${formatBtc(btcPrice)} / ${formatCurrency(pipe.unitPriceUsd)}</span>
                            <span class="token-value">Total: <span style="color: var(--accent-light)">${formatBtc(btcTotal)} / ${formatCurrency(pipe.totalValueUsd)}</span></span>
                        </div>`;
            });
            
            if (sortedPipe.length > 2) {
                html += `<div class="more-tokens" style="margin-top: 8px; font-style: italic; text-align: center; padding: 4px; background: rgba(var(--bg-color-rgb), 0.3); border-radius: 4px;">+ ${sortedPipe.length - 2} more tokens</div>`;
            }
            
            html += `</div></div>`;
        }
        
        // BRC-20 Tokens
        const brc20Stats = protocolStats.brc20 || {};
        if (walletResult.brc20 && walletResult.brc20.length > 0) {
            html += `<div class="protocol-section" style="margin-bottom: 16px; background: rgba(var(--bg-color-rgb), 0.5); padding: 10px; border-radius: 8px; border-left: 3px solid #f7931a;">
                <div class="protocol-header" style="font-weight: bold; margin-bottom: 8px;"><strong>BRC-20</strong> (<span style="color: var(--accent-light)">${walletResult.brc20.length}</span>) - <span style="color: var(--accent-light)">${formatBtc(brc20Stats.totalValueBtc ?? 0)} - ${formatCurrency(brc20Stats.totalValueUsd ?? 0)}</span></div>
                <div class="tokens-list" style="padding-left: 10px;">`;
            
            // Sort tokens by total value in descending order
            const sortedBrc20 = [...walletResult.brc20].sort((a, b) => b.totalValue - a.totalValue);
            
            // Limiter à 2 tokens pour ne pas surcharger l'UI
            const topBrc20 = sortedBrc20.slice(0, 2);
            topBrc20.forEach(token => {
                const btcPrice = token.unitPrice;
                const btcTotal = token.totalValue;
                html += `<div class="token" style="margin-bottom: 6px; padding: 4px 0; border-bottom: 1px dotted var(--border-color);">
                            <span class="token-name">${token.ticker.toUpperCase()} (<span style="color: var(--accent-light)">${token.quantity.toLocaleString()}</span>)</span>
                            <span class="token-price">Unit: ${formatBtc(btcPrice)} / ${formatCurrency(token.unitPriceUsd)}</span>
                            <span class="token-value">Total: <span style="color: var(--accent-light)">${formatBtc(btcTotal)} / ${formatCurrency(token.totalValueUsd)}</span></span>
                        </div>`;
            });
            
            if (sortedBrc20.length > 2) {
                html += `<div class="more-tokens" style="margin-top: 8px; font-style: italic; text-align: center; padding: 4px; background: rgba(var(--bg-color-rgb), 0.3); border-radius: 4px;">+ ${sortedBrc20.length - 2} more tokens</div>`;
            }
            
            html += `</div></div>`;
        }
        
        // RUNES
        const runesStats = protocolStats.runes || {};
        if (walletResult.runes && walletResult.runes.length > 0) {
            html += `<div class="protocol-section" style="margin-bottom: 16px; background: rgba(var(--bg-color-rgb), 0.5); padding: 10px; border-radius: 8px; border-left: 3px solid #6b5b95;">
                <div class="protocol-header" style="font-weight: bold; margin-bottom: 8px;"><strong>RUNES</strong> (<span style="color: var(--accent-light)">${walletResult.runes.length}</span>) - <span style="color: var(--accent-light)">${formatBtc(runesStats.totalValueBtc ?? 0)} - ${formatCurrency(runesStats.totalValueUsd || 0)}</span></div>
                <div class="tokens-list" style="padding-left: 10px;">`;
            
            // Sort runes by total value in descending order
            const sortedRunes = [...walletResult.runes].sort((a, b) => b.totalValue - a.totalValue);
            
            // Limiter à 2 tokens pour ne pas surcharger l'UI
            const topRunes = sortedRunes.slice(0, 2);
            topRunes.forEach(rune => {
                const btcPrice = rune.unitPrice;
                const btcTotal = rune.totalValue;
                const symbolDisplay = rune.symbol ? ` ${rune.symbol}` : '';
                html += `<div class="token" style="margin-bottom: 6px; padding: 4px 0; border-bottom: 1px dotted var(--border-color);">
                            <span class="token-name">${rune.ticker.toUpperCase()}${symbolDisplay} (<span style="color: var(--accent-light)">${rune.quantity.toLocaleString()}</span>)</span>
                            <span class="token-price">Unit: ${formatBtc(btcPrice)} / ${formatCurrency(rune.unitPriceUsd)}</span>
                            <span class="token-value">Total: <span style="color: var(--accent-light)">${formatBtc(btcTotal)} / ${formatCurrency(rune.totalValueUsd)}</span></span>
                        </div>`;
            });
            
            if (sortedRunes.length > 2) {
                html += `<div class="more-tokens" style="margin-top: 8px; font-style: italic; text-align: center; padding: 4px; background: rgba(var(--bg-color-rgb), 0.3); border-radius: 4px;">+ ${sortedRunes.length - 2} more tokens</div>`;
            }
            
            html += `</div></div>`;
        }
        
        // TAP Tokens
        const tapStats = protocolStats.tap || {};
        if (walletResult.tap && walletResult.tap.length > 0) {
            html += `<div class="protocol-section" style="margin-bottom: 16px; background: rgba(var(--bg-color-rgb), 0.5); padding: 10px; border-radius: 8px; border-left: 3px solid #78c2ad;">
                <div class="protocol-header" style="font-weight: bold; margin-bottom: 8px;"><strong>TAP</strong> (<span style="color: var(--accent-light)">${walletResult.tap.length}</span>) - <span style="color: var(--accent-light)">${formatBtc(tapStats.totalValueBtc ?? 0)} - ${formatCurrency(tapStats.totalValueUsd ?? 0)}</span></div>
                <div class="tokens-list" style="padding-left: 10px;">`;
            
            // Sort tokens by total value in descending order
            const sortedTap = [...walletResult.tap].sort((a, b) => b.totalValue - a.totalValue);
            
            // Limiter à 2 tokens pour ne pas surcharger l'UI
            const topTap = sortedTap.slice(0, 2);
            topTap.forEach(token => {
                const btcPrice = token.unitPrice;
                const btcTotal = token.totalValue;
                html += `<div class="token" style="margin-bottom: 6px; padding: 4px 0; border-bottom: 1px dotted var(--border-color);">
                            <span class="token-name">${token.ticker.toUpperCase()} (<span style="color: var(--accent-light)">${token.quantity.toLocaleString()}</span>)</span>
                            <span class="token-price">Unit: ${formatBtc(btcPrice)} / ${formatCurrency(token.unitPriceUsd)}</span>
                            <span class="token-value">Total: <span style="color: var(--accent-light)">${formatBtc(btcTotal)} / ${formatCurrency(token.totalValueUsd)}</span></span>
                        </div>`;
            });
            
            if (sortedTap.length > 2) {
                html += `<div class="more-tokens" style="margin-top: 8px; font-style: italic; text-align: center; padding: 4px; background: rgba(var(--bg-color-rgb), 0.3); border-radius: 4px;">+ ${sortedTap.length - 2} more tokens</div>`;
            }
            
            html += `</div></div>`;
        }
        
        html += `</div></div>`;
    });
    
    html += `</div></div>`;
    return html;
}

// Export functions and variables
export {
    setupConversations,
    createNewConversation,
    loadConversation,
    saveConversations,
    loadConversations,
    renderConversationsList,
    renderMessages,
    deleteConversation,
    reportInaccurateContent,
    addThinkingIndicator,
    removeThinkingIndicator,
    getConversations,
    createWalletScanResultHTML,
    createMultipleWalletScanResultHTML
};